import React, { useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { 
  IoFastFoodOutline, 
  IoRestaurantOutline, 
  IoTrendingUpOutline,
  IoCartOutline,
  IoWalletOutline,
  IoArrowUpCircleOutline,
  IoCloseCircleOutline,
  IoThumbsDownOutline,
  IoTimeOutline,
  IoIceCreamOutline,
  IoBanOutline,
  IoHappyOutline,
} from "react-icons/io5";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

export default function OrderAnalytics({ orderData = {}, currency = "₺", orderSizes = {}, maxOrderAmount = 0, tipFeedback = null, weeklyDayTraffic = null, dayHourTraffic = null, topDayHour = null }) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const subtleText = useColorModeValue("gray.600", "gray.400");
  const bgStat = useColorModeValue("gray.50", "navy.700");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgIcon = useColorModeValue("white", "whiteAlpha.200");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const boxShadow = useColorModeValue(
    "0px 2px 5px rgba(0, 0, 0, 0.05)",
    "0px 2px 5px rgba(255, 255, 255, 0.05)"
  );
  const cancelledColor = useColorModeValue("red.500", "red.300");

  // Güvenli erişim için yardımcı değişkenler
  const safeOrderSizes = orderSizes || {
    small: { percentage: 0, avgAmount: 0, count: 0 },
    medium: { percentage: 0, avgAmount: 0, count: 0 },
    large: { percentage: 0, avgAmount: 0, count: 0 }
  };
  
  const safeOrderData = orderData || {
    totalOrders: 0,
    activeOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    averageAmount: 0
  };

  // En popüler sipariş türünü belirle
  const getMostPopularOrderSize = () => {
    if (!safeOrderSizes) return "Veri yok";
    
    const { small, medium, large } = safeOrderSizes;
    
    if (small.percentage >= medium.percentage && small.percentage >= large.percentage) {
      return `Küçük Boy (%${small.percentage})`;
    } else if (medium.percentage >= large.percentage) {
      return `Orta Boy (%${medium.percentage})`;
    } else {
      return `Büyük Boy (%${large.percentage})`;
    }
  };

  // Sayısal değerleri düzgün formatlama
  const formatNumber = (value) => {
    if (value === undefined || value === null) return "0";
    if (typeof value === "number") return value.toFixed(2);
    return value;
  };

  // İptal oranını hesapla
  const calculateCancelRate = () => {
    const total = safeOrderData.totalOrders || 0;
    const cancelled = safeOrderData.cancelledOrders || 0;
    
    if (total === 0) return 0;
    return ((cancelled / total) * 100).toFixed(1);
  };

  return (
    <Card p="20px" align="center" direction="column" w="100%" h="100%">
      <Flex justify="space-between" align="center" mb="20px" w="100%">
        <Box>
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Sipariş Analizi
          </Text>
          <Text color={subtleText} fontSize="xs" mt={1}>
            Bu Ayın Verileri
          </Text>
        </Box>
      </Flex>

      {!safeOrderData ? (
        <Flex direction="column" align="center" justify="center" h="100%" py="20px">
          <Text fontSize="md" color={subtleText}>
            Henüz sipariş verisi bulunmuyor.
          </Text>
        </Flex>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px" mb="20px">
            <Stat bg={bgStat} p="15px" borderRadius="12px" boxShadow={boxShadow}>
              <Flex mb="5px">
                <StatLabel fontSize="sm" color={subtleText}>
                  Toplam Sipariş
                </StatLabel>
                <Flex ms="auto" align="center">
                  <Icon as={IoCartOutline} color={iconColor} w="15px" h="15px" />
                </Flex>
              </Flex>
              <StatNumber color={textColor} fontSize="lg">
                {safeOrderData.totalOrders || 0}
              </StatNumber>
              <Flex align="center" mt="8px">
                <Text color="green.500" fontSize="sm" fontWeight="500" me="5px">
                  {safeOrderData.activeOrders || 0} aktif
                </Text>
                <Text color={subtleText} fontSize="sm" fontWeight="400">
                  sipariş
                </Text>
              </Flex>
            </Stat>

            <Stat bg={bgStat} p="15px" borderRadius="12px" boxShadow={boxShadow}>
              <Flex mb="5px">
                <StatLabel fontSize="sm" color={subtleText}>
                  Tamamlanan Sipariş
                </StatLabel>
                <Flex ms="auto" align="center">
                  <Icon as={IoWalletOutline} color={iconColor} w="15px" h="15px" />
                </Flex>
              </Flex>
              <StatNumber color={textColor} fontSize="lg">
                {safeOrderData.deliveredOrders || 0}
              </StatNumber>
              <Flex align="center" mt="8px">
                <Text color="green.500" fontSize="sm" fontWeight="500" me="5px">
                  %{((safeOrderData.deliveredOrders / safeOrderData.totalOrders) * 100 || 0).toFixed(1)}
                </Text>
                <Text color={subtleText} fontSize="sm" fontWeight="400">
                  tamamlanma oranı
                </Text>
              </Flex>
            </Stat>

            <Stat bg={bgStat} p="15px" borderRadius="12px" boxShadow={boxShadow}>
              <Flex mb="5px">
                <StatLabel fontSize="sm" color={subtleText}>
                  İptal Edilen Sipariş
                </StatLabel>
                <Flex ms="auto" align="center">
                  <Icon as={IoCloseCircleOutline} color={cancelledColor} w="15px" h="15px" />
                </Flex>
              </Flex>
              <StatNumber color={textColor} fontSize="lg">
                {safeOrderData.cancelledOrders || 0}
              </StatNumber>
              <Flex align="center" mt="8px">
                <Text color={cancelledColor} fontSize="sm" fontWeight="500" me="5px">
                  %{calculateCancelRate()}
                </Text>
                <Text color={subtleText} fontSize="sm" fontWeight="400">
                  iptal oranı
                </Text>
              </Flex>
            </Stat>
          </SimpleGrid>

          <Divider mb="20px" />

          <Text color={textColor} fontSize="md" fontWeight="700" mb="15px" textAlign="left" w="100%">
            Sipariş Büyüklük Analizi
          </Text>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px" mb="20px">
            <Flex
              direction="column"
              align="center"
              justify="center"
              bg={bgStat}
              p="15px"
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Flex
                align="center"
                justify="center"
                bg={bgIcon}
                borderRadius="50%"
                w="50px"
                h="50px"
                mb="12px"
              >
                <Icon as={IoFastFoodOutline} color="green.500" boxSize={6} />
              </Flex>
              <Text fontSize="md" fontWeight="700" mb="4px">
                Küçük Boy
              </Text>
              <Text fontSize="sm" fontWeight="500">
                %{safeOrderSizes?.small?.percentage || 0}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="green.500" mt="8px">
                Ort. {currency}{formatNumber(safeOrderSizes?.small?.avgAmount)}
              </Text>
              <Text fontSize="xs" color={subtleText} mt="4px">
                ({safeOrderSizes?.small?.count || 0} sipariş)
              </Text>
            </Flex>

            <Flex
              direction="column"
              align="center"
              justify="center"
              bg={bgStat}
              p="15px"
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Flex
                align="center"
                justify="center"
                bg={bgIcon}
                borderRadius="50%"
                w="50px"
                h="50px"
                mb="12px"
              >
                <Icon as={IoFastFoodOutline} color="orange.500" boxSize={6} />
              </Flex>
              <Text fontSize="md" fontWeight="700" mb="4px">
                Orta Boy
              </Text>
              <Text fontSize="sm" fontWeight="500">
                %{safeOrderSizes?.medium?.percentage || 0}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="orange.500" mt="8px">
                Ort. {currency}{formatNumber(safeOrderSizes?.medium?.avgAmount)}
              </Text>
              <Text fontSize="xs" color={subtleText} mt="4px">
                ({safeOrderSizes?.medium?.count || 0} sipariş)
              </Text>
            </Flex>

            <Flex
              direction="column"
              align="center"
              justify="center"
              bg={bgStat}
              p="15px"
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Flex
                align="center"
                justify="center"
                bg={bgIcon}
                borderRadius="50%"
                w="50px"
                h="50px"
                mb="12px"
              >
                <Icon as={IoFastFoodOutline} color="purple.500" boxSize={6} />
              </Flex>
              <Text fontSize="md" fontWeight="700" mb="4px">
                Büyük Boy
              </Text>
              <Text fontSize="sm" fontWeight="500">
                %{safeOrderSizes?.large?.percentage || 0}
              </Text>
              <Text fontSize="sm" fontWeight="medium" color="purple.500" mt="8px">
                Ort. {currency}{formatNumber(safeOrderSizes?.large?.avgAmount)}
              </Text>
              <Text fontSize="xs" color={subtleText} mt="4px">
                ({safeOrderSizes?.large?.count || 0} sipariş)
              </Text>
            </Flex>
          </SimpleGrid>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px" mt="20px">
            <Flex 
              align="center" 
              bg={bgStat} 
              p="16px" 
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Icon as={IoRestaurantOutline} color="purple.500" boxSize={6} mr={3} />
              <Box>
                <Text fontSize="sm" color="gray.500">En Popüler Sipariş</Text>
                <Text fontSize="md" fontWeight="bold">
                  {getMostPopularOrderSize()}
                </Text>
              </Box>
            </Flex>
            
            <Flex 
              align="center" 
              bg={bgStat} 
              p="16px" 
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Icon as={IoTrendingUpOutline} color="green.500" boxSize={6} mr={3} />
              <Box>
                <Text fontSize="sm" color="gray.500">Ortalama Sipariş Tutarı</Text>
                <Text fontSize="md" fontWeight="bold">{currency}{formatNumber(safeOrderData?.averageAmount || 0)}</Text>
              </Box>
            </Flex>
            
            <Flex 
              align="center" 
              bg={bgStat} 
              p="16px" 
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Icon as={IoArrowUpCircleOutline} color="purple.500" boxSize={6} mr={3} />
              <Box>
                <Text fontSize="sm" color="gray.500">En Yüksek Sipariş</Text>
                <Text fontSize="md" fontWeight="bold">{currency}{formatNumber(maxOrderAmount || 0)}</Text>
              </Box>
            </Flex>
          </SimpleGrid>

          {/* Haftalık Gün Analizi */}
          {weeklyDayTraffic && weeklyDayTraffic.length > 0 && (
            <>
              <Divider my="20px" />
              <Text color={textColor} fontSize="md" fontWeight="700" mb="15px" textAlign="left" w="100%">
                Haftalık Gün Analizi
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px" mb="20px">
                {/* Bar Chart */}
                <Box bg={bgStat} p="20px" borderRadius="12px" boxShadow={boxShadow}>
                  <Text fontSize="sm" color={subtleText} mb="15px" textAlign="center">
                    Günlere Göre Sipariş Dağılımı
                  </Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={weeklyDayTraffic}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dayName" />
                      <YAxis />
                      <RechartsTooltip 
                        formatter={(value, name) => {
                          if (name === 'orderCount') {
                            return [`${value} sipariş`, 'Sipariş Sayısı'];
                          }
                          if (name === 'revenue') {
                            return [`${currency}${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Gelir'];
                          }
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="orderCount" fill="#4299E1" name="Sipariş Sayısı" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* En Yoğun Günler */}
                <Box bg={bgStat} p="20px" borderRadius="12px" boxShadow={boxShadow}>
                  <Text fontSize="sm" color={subtleText} mb="15px" textAlign="center">
                    En Yoğun Günler
                  </Text>
                  <Flex direction="column" gap="12px">
                    {weeklyDayTraffic
                      .sort((a, b) => b.orderCount - a.orderCount)
                      .slice(0, 3)
                      .map((day, index) => (
                        <Flex 
                          key={day.day}
                          align="center" 
                          justify="space-between" 
                          p="12px" 
                          bg={bgIcon} 
                          borderRadius="8px"
                        >
                          <Flex align="center">
                            <Box
                              w="30px"
                              h="30px"
                              borderRadius="full"
                              bg={index === 0 ? "yellow.400" : index === 1 ? "gray.300" : "orange.300"}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              mr={3}
                              fontWeight="bold"
                              color="white"
                            >
                              {index + 1}
                            </Box>
                            <Text fontSize="sm" fontWeight="600">{day.dayName}</Text>
                          </Flex>
                          <Box textAlign="right">
                            <Text fontSize="sm" fontWeight="700" color={textColor}>
                              {day.orderCount} sipariş
                            </Text>
                            <Text fontSize="xs" color={subtleText}>
                              {currency}{day.revenue?.toLocaleString('tr-TR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) || '0.00'}
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                  </Flex>
                </Box>
              </SimpleGrid>
            </>
          )}

          {/* Saatlik Analiz */}
          {dayHourTraffic && dayHourTraffic.length > 0 && (
            <>
              <Divider my="20px" />
              <Text color={textColor} fontSize="md" fontWeight="700" mb="15px" textAlign="left" w="100%">
                Saatlik Yoğunluk Analizi
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px" mb="20px">
                {/* En Yoğun Saatler Tablosu */}
                <Box bg={bgStat} p="20px" borderRadius="12px" boxShadow={boxShadow}>
                  <Text fontSize="sm" color={subtleText} mb="15px" textAlign="center">
                    En Yoğun Gün ve Saatler
                  </Text>
                  {topDayHour && (
                    <Box mb="15px" p="12px" bg={bgIcon} borderRadius="8px" textAlign="center">
                      <Text fontSize="xs" color={subtleText} mb="4px">
                        En Yoğun Saat
                      </Text>
                      <Text fontSize="lg" fontWeight="700" color={textColor}>
                        {topDayHour.dayName} {topDayHour.hourLabel}
                      </Text>
                      <Text fontSize="sm" color={subtleText} mt="4px">
                        {topDayHour.orderCount} sipariş
                      </Text>
                    </Box>
                  )}
                  <Box maxH="400px" overflowY="auto">
                    <Table variant="simple" size="sm">
                      <Thead position="sticky" top={0} bg={bgStat} zIndex={1}>
                        <Tr>
                          <Th borderColor={borderColor} fontSize="xs">Gün</Th>
                          <Th borderColor={borderColor} fontSize="xs">Saat</Th>
                          <Th borderColor={borderColor} isNumeric fontSize="xs">Sipariş</Th>
                          <Th borderColor={borderColor} isNumeric fontSize="xs">Gelir</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {dayHourTraffic.slice(0, 15).map((item, index) => (
                          <Tr key={`${item.day}-${item.hour}-${index}`}>
                            <Td borderColor={borderColor} fontSize="xs">{item.dayName}</Td>
                            <Td borderColor={borderColor} fontSize="xs" fontWeight="600">{item.hourLabel}</Td>
                            <Td isNumeric borderColor={borderColor} fontSize="xs">{item.orderCount}</Td>
                            <Td isNumeric borderColor={borderColor} fontSize="xs" color="green.500">
                              {currency}{item.revenue?.toLocaleString('tr-TR', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }) || '0'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>

                {/* Saatlik Dağılım Grafiği */}
                <Box bg={bgStat} p="20px" borderRadius="12px" boxShadow={boxShadow}>
                  <Text fontSize="sm" color={subtleText} mb="15px" textAlign="center">
                    Saatlik Sipariş Dağılımı
                  </Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dayHourTraffic.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="hourLabel" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={10}
                      />
                      <YAxis />
                      <RechartsTooltip 
                        formatter={(value, name) => {
                          if (name === 'orderCount') {
                            return [`${value} sipariş`, 'Sipariş Sayısı'];
                          }
                          return [value, name];
                        }}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0]) {
                            return `${payload[0].payload.dayName} ${label}`;
                          }
                          return label;
                        }}
                      />
                      <Legend />
                      <Bar dataKey="orderCount" fill="#4299E1" name="Sipariş Sayısı" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </SimpleGrid>
            </>
          )}

          {/* Tip Feedback Analizi */}
          {tipFeedback && tipFeedback.withoutTip > 0 && (
            <>
              <Divider my="20px" />
              <Text color={textColor} fontSize="md" fontWeight="700" mb="15px" textAlign="left" w="100%">
                Bahşiş Vermeme Nedenleri
              </Text>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px" mb="20px">
                {/* Pie Chart */}
                <Box bg={bgStat} p="20px" borderRadius="12px" boxShadow={boxShadow}>
                  <Text fontSize="sm" color={subtleText} mb="15px" textAlign="center">
                    Bahşiş Vermeyenlerin Dağılımı
                  </Text>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: tipFeedback.reasons.service_slow.label, value: tipFeedback.reasons.service_slow.count, color: "#E53E3E" },
                          { name: tipFeedback.reasons.food_cold.label, value: tipFeedback.reasons.food_cold.count, color: "#3182CE" },
                          { name: tipFeedback.reasons.principle.label, value: tipFeedback.reasons.principle.count, color: "#805AD5" },
                          { name: tipFeedback.reasons.satisfied.label, value: tipFeedback.reasons.satisfied.count, color: "#38A169" },
                          { name: tipFeedback.reasons.no_feedback.label, value: tipFeedback.reasons.no_feedback.count, color: "#718096" },
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[
                          { name: tipFeedback.reasons.service_slow.label, value: tipFeedback.reasons.service_slow.count, color: "#E53E3E" },
                          { name: tipFeedback.reasons.food_cold.label, value: tipFeedback.reasons.food_cold.count, color: "#3182CE" },
                          { name: tipFeedback.reasons.principle.label, value: tipFeedback.reasons.principle.count, color: "#805AD5" },
                          { name: tipFeedback.reasons.satisfied.label, value: tipFeedback.reasons.satisfied.count, color: "#38A169" },
                          { name: tipFeedback.reasons.no_feedback.label, value: tipFeedback.reasons.no_feedback.count, color: "#718096" },
                        ].filter(item => item.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Detaylı Liste */}
                <Box bg={bgStat} p="20px" borderRadius="12px" boxShadow={boxShadow}>
                  <Text fontSize="sm" color={subtleText} mb="15px" textAlign="center">
                    Detaylı İstatistikler
                  </Text>
                  <Flex direction="column" gap="12px">
                    {tipFeedback.reasons.service_slow.count > 0 && (
                      <Flex align="center" justify="space-between" p="12px" bg={bgIcon} borderRadius="8px">
                        <Flex align="center">
                          <Icon as={IoTimeOutline} color="red.500" boxSize={5} mr={2} />
                          <Text fontSize="sm" fontWeight="500">{tipFeedback.reasons.service_slow.label}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="700">
                          {tipFeedback.reasons.service_slow.count} (%{tipFeedback.reasons.service_slow.percentage})
                        </Text>
                      </Flex>
                    )}
                    {tipFeedback.reasons.food_cold.count > 0 && (
                      <Flex align="center" justify="space-between" p="12px" bg={bgIcon} borderRadius="8px">
                        <Flex align="center">
                          <Icon as={IoIceCreamOutline} color="blue.500" boxSize={5} mr={2} />
                          <Text fontSize="sm" fontWeight="500">{tipFeedback.reasons.food_cold.label}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="700">
                          {tipFeedback.reasons.food_cold.count} (%{tipFeedback.reasons.food_cold.percentage})
                        </Text>
                      </Flex>
                    )}
                    {tipFeedback.reasons.principle.count > 0 && (
                      <Flex align="center" justify="space-between" p="12px" bg={bgIcon} borderRadius="8px">
                        <Flex align="center">
                          <Icon as={IoBanOutline} color="purple.500" boxSize={5} mr={2} />
                          <Text fontSize="sm" fontWeight="500">{tipFeedback.reasons.principle.label}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="700">
                          {tipFeedback.reasons.principle.count} (%{tipFeedback.reasons.principle.percentage})
                        </Text>
                      </Flex>
                    )}
                    {tipFeedback.reasons.satisfied.count > 0 && (
                      <Flex align="center" justify="space-between" p="12px" bg={bgIcon} borderRadius="8px">
                        <Flex align="center">
                          <Icon as={IoHappyOutline} color="green.500" boxSize={5} mr={2} />
                          <Text fontSize="sm" fontWeight="500">{tipFeedback.reasons.satisfied.label}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="700">
                          {tipFeedback.reasons.satisfied.count} (%{tipFeedback.reasons.satisfied.percentage})
                        </Text>
                      </Flex>
                    )}
                    {tipFeedback.reasons.no_feedback.count > 0 && (
                      <Flex align="center" justify="space-between" p="12px" bg={bgIcon} borderRadius="8px">
                        <Flex align="center">
                          <Icon as={IoThumbsDownOutline} color="gray.500" boxSize={5} mr={2} />
                          <Text fontSize="sm" fontWeight="500">{tipFeedback.reasons.no_feedback.label}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="700">
                          {tipFeedback.reasons.no_feedback.count} (%{tipFeedback.reasons.no_feedback.percentage})
                        </Text>
                      </Flex>
                    )}
                  </Flex>
                  <Divider my="15px" />
                  <Flex justify="space-between" align="center" mt="10px">
                    <Text fontSize="sm" color={subtleText}>Bahşiş Verenler:</Text>
                    <Text fontSize="md" fontWeight="700" color="green.500">
                      {tipFeedback.withTip} sipariş
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="center" mt="5px">
                    <Text fontSize="sm" color={subtleText}>Bahşiş Vermeyenler:</Text>
                    <Text fontSize="md" fontWeight="700" color="red.500">
                      {tipFeedback.withoutTip} sipariş
                    </Text>
                  </Flex>
                </Box>
              </SimpleGrid>
            </>
          )}
        </>
      )}
    </Card>
  );
} 